import { useEffect, useState } from "react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Users, Building2, CreditCard, AlertTriangle, MessageSquare, CheckCircle2, XCircle } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const [tab, setTab] = useState("overview");
  const [a, setA] = useState({});
  const [users, setUsers] = useState([]);
  const [props, setProps] = useState([]);
  const [bookings, setBookings] = useState([]);
  const [complaints, setComplaints] = useState([]);
  const [messages, setMessages] = useState([]);

  const fetchAll = async () => {
    try {
      const [an, u, p, b, c, m] = await Promise.all([
        api.get("/admin/analytics"),
        api.get("/admin/users"),
        api.get("/properties/admin/all"),
        api.get("/bookings"),
        api.get("/complaints"),
        api.get("/contact/messages"),
      ]);
      setA(an.data); setUsers(u.data); setProps(p.data); setBookings(b.data); setComplaints(c.data); setMessages(m.data);
    } catch (e) { /* unauthorized */ }
  };

  useEffect(() => { if (user && user.role === "admin") fetchAll(); }, [user]);

  if (loading) return <div className="max-w-7xl mx-auto px-5 py-20">Loading...</div>;
  if (!user || typeof user !== "object" || user.role !== "admin") {
    return (
      <div className="max-w-3xl mx-auto px-5 py-20 text-center">
        <h1 className="font-display font-black text-4xl">Admins only</h1>
        <p className="text-[#4A4A4A] mt-2">Sign in with an admin account to access this panel.</p>
        <Link to="/login" className="brut-btn mt-6 inline-flex">Sign in</Link>
      </div>
    );
  }

  const approveProperty = async (pid, status) => {
    await api.patch(`/properties/${pid}/status`, null, { params: { status } }).catch(()=>toast.error("Failed"));
    toast.success(`Property ${status}`);
    fetchAll();
  };
  const verifyUser = async (uid, verified) => {
    await api.patch(`/admin/users/${uid}`, null, { params: { verified, kyc_status: verified ? "approved" : "rejected" } }).catch(()=>toast.error("Failed"));
    toast.success("Updated");
    fetchAll();
  };

  const tabs = [
    {id:"overview", label:"Overview", icon:<Users size={14}/>},
    {id:"users", label:"Users", icon:<Users size={14}/>},
    {id:"properties", label:"Properties", icon:<Building2 size={14}/>},
    {id:"bookings", label:"Bookings", icon:<CreditCard size={14}/>},
    {id:"complaints", label:"Complaints", icon:<AlertTriangle size={14}/>},
    {id:"messages", label:"Contact Msgs", icon:<MessageSquare size={14}/>},
  ];

  const cityData = (() => {
    const map = {};
    props.forEach(p => { map[p.city] = (map[p.city]||0)+1; });
    return Object.entries(map).map(([city, count]) => ({ city, count }));
  })();

  return (
    <div className="panel-shell">
    <div className="max-w-7xl mx-auto px-5 md:px-10 py-10" data-testid="admin-page">
      <div className="brut-card p-6 bg-[#FFEAF6]">
        <span className="brut-tag pink">Admin Panel</span>
        <h1 className="font-display font-black text-3xl md:text-4xl mt-2">Hostro Control Center</h1>
      </div>

      <div className="flex flex-wrap gap-2 mt-6">
        {tabs.map(t => (
          <button key={t.id} onClick={()=>setTab(t.id)} className={`brut-tag ${tab===t.id?"lime":"white"} cursor-pointer flex items-center gap-1`} data-testid={`admin-tab-${t.id}`}>
            {t.icon} {t.label}
          </button>
        ))}
      </div>

      <div className="mt-8">
        {tab === "overview" && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="admin-overview">
              <div className="brut-card p-5"><div className="brut-tag lime">Users</div><div className="font-display font-black text-4xl mt-2">{a.users||0}</div><div className="text-xs mt-1">{a.students||0} students · {a.owners||0} owners</div></div>
              <div className="brut-card p-5"><div className="brut-tag pink">Properties</div><div className="font-display font-black text-4xl mt-2">{a.properties||0}</div><div className="text-xs mt-1">{a.pending_properties||0} pending</div></div>
              <div className="brut-card p-5"><div className="brut-tag blue">Bookings</div><div className="font-display font-black text-4xl mt-2">{a.bookings||0}</div><div className="text-xs mt-1">{a.paid_bookings||0} paid</div></div>
              <div className="brut-card p-5 bg-[#2ED573]"><div className="brut-tag white">Revenue (₹)</div><div className="font-display font-black text-4xl mt-2">{(a.revenue||0).toLocaleString()}</div><div className="text-xs mt-1">all-time</div></div>
            </div>
            <div className="brut-card p-5 mt-6">
              <h3 className="font-display font-black text-2xl mb-4">Properties by city</h3>
              <ResponsiveContainer width="100%" height={250}>
                <BarChart data={cityData}>
                  <CartesianGrid stroke="#0F1F17" strokeDasharray="3 3" opacity={0.2}/>
                  <XAxis dataKey="city" tick={{fontWeight:700}}/>
                  <YAxis allowDecimals={false}/>
                  <Tooltip contentStyle={{border:"2px solid #0F1F17", borderRadius:0, fontFamily:"Outfit"}}/>
                  <Bar dataKey="count" fill="#2ED573" stroke="#0F1F17" strokeWidth={2}/>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </>
        )}

        {tab === "users" && (
          <div className="brut-card overflow-x-auto" data-testid="admin-users">
            <table className="w-full text-sm">
              <thead className="bg-[#ECFAF1] border-b-2 border-[#0F1F17]">
                <tr><th className="p-3 text-left">Name</th><th className="p-3 text-left">Email</th><th className="p-3 text-left">Role</th><th className="p-3 text-left">KYC</th><th className="p-3 text-left">Verified</th><th className="p-3 text-right">Action</th></tr>
              </thead>
              <tbody>
                {users.map(u => (
                  <tr key={u.id} className="border-b border-[#0F1F17]/20" data-testid={`admin-user-${u.id}`}>
                    <td className="p-3 font-medium">{u.name}</td>
                    <td className="p-3 text-[#4A4A4A]">{u.email}</td>
                    <td className="p-3"><span className="brut-tag white !text-[10px]">{u.role}</span></td>
                    <td className="p-3"><span className={`brut-tag !text-[10px] ${u.kyc_status==="approved"?"lime":u.kyc_status==="rejected"?"pink":"white"}`}>{u.kyc_status||"pending"}</span></td>
                    <td className="p-3">{u.verified ? <CheckCircle2 size={16} className="text-green-700"/> : <XCircle size={16} className="text-[#FF90E8]"/>}</td>
                    <td className="p-3 text-right">
                      <button className="brut-btn !py-1 !px-2 !text-xs mr-2" onClick={()=>verifyUser(u.id, true)}>Verify</button>
                      <button className="brut-btn pink !py-1 !px-2 !text-xs" onClick={()=>verifyUser(u.id, false)}>Reject</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {tab === "properties" && (
          <div className="space-y-3" data-testid="admin-properties">
            {props.map(p => (
              <div key={p.id} className="brut-card p-4 flex flex-wrap items-center gap-3">
                <img src={p.images?.[0]} alt="" className="w-20 h-20 object-cover brut-border"/>
                <div className="flex-1 min-w-[200px]">
                  <div className="font-bold">{p.title}</div>
                  <div className="text-xs text-[#4A4A4A]">{p.city} · ₹{p.price_per_month} · {p.gender}</div>
                </div>
                <span className={`brut-tag ${p.status==="active"||p.status==="approved"?"lime":p.status==="rejected"?"pink":"white"}`}>{p.status}</span>
                <div className="flex gap-2">
                  <button className="brut-btn !py-1 !px-2 !text-xs" onClick={()=>approveProperty(p.id, "approved")}>Approve</button>
                  <button className="brut-btn pink !py-1 !px-2 !text-xs" onClick={()=>approveProperty(p.id, "rejected")}>Reject</button>
                </div>
              </div>
            ))}
          </div>
        )}

        {tab === "bookings" && (
          <div className="space-y-3" data-testid="admin-bookings">
            {bookings.map(b => (
              <div key={b.id} className="brut-card p-4 flex flex-wrap gap-3 items-center">
                <div className="flex-1 min-w-[200px]">
                  <div className="font-bold">{b.property_title}</div>
                  <div className="text-xs text-[#4A4A4A]">{b.student_name} · move-in {b.move_in_date}</div>
                </div>
                <span className="brut-tag white">₹{b.total_amount}</span>
                <span className={`brut-tag ${b.payment_status==="paid"?"lime":"pink"}`}>{b.payment_status}</span>
                <span className="brut-tag white">{b.status}</span>
              </div>
            ))}
            {bookings.length === 0 && <div className="brut-card p-8 text-center text-[#4A4A4A]">No bookings yet.</div>}
          </div>
        )}

        {tab === "complaints" && (
          <div className="space-y-3" data-testid="admin-complaints">
            {complaints.map(c => (
              <div key={c.id} className="brut-card p-4">
                <div className="flex justify-between flex-wrap gap-2">
                  <strong>{c.subject}</strong>
                  <span className={`brut-tag ${c.priority==="high"?"pink":"white"}`}>{c.priority}</span>
                </div>
                <div className="text-xs text-[#4A4A4A] mt-1">{c.user_name} · {new Date(c.created_at).toLocaleDateString()}</div>
                <p className="mt-2 text-sm">{c.description}</p>
              </div>
            ))}
            {complaints.length === 0 && <div className="brut-card p-8 text-center text-[#4A4A4A]">No complaints.</div>}
          </div>
        )}

        {tab === "messages" && (
          <div className="space-y-3" data-testid="admin-messages">
            {messages.map(m => (
              <div key={m.id} className="brut-card p-4">
                <div className="flex justify-between flex-wrap gap-2">
                  <strong>{m.subject}</strong>
                  <span className="text-xs">{new Date(m.created_at).toLocaleDateString()}</span>
                </div>
                <div className="text-xs text-[#4A4A4A] mt-1">{m.name} · {m.email} · {m.phone}</div>
                <p className="mt-2 text-sm whitespace-pre-line">{m.message}</p>
              </div>
            ))}
            {messages.length === 0 && <div className="brut-card p-8 text-center text-[#4A4A4A]">No messages.</div>}
          </div>
        )}
      </div>
    </div>
    </div>
  );
}
